import { BadgeColor } from '@teetsh/app/src/types/colors';

// Regex to find tags globally, capturing name (1) and optional color (2)
const TAG_FIND_REGEX = /<<([^>|]+)(?:\|([^>]+))?>>/g;
const validBadgeColors: BadgeColor[] = [
  'red',
  'blue',
  'gray',
  'green',
  'pink',
  'yellow',
  'indigo',
];

export interface ParsedSegment {
  type: 'tag' | 'text';
  content?: string;
  name?: string;
  color?: BadgeColor;
}

// Function to parse label into tags and text segments
export function referentialLabelParser(label: string = ''): ParsedSegment[] {
  const segments: ParsedSegment[] = [];
  let lastIndex = 0;
  let match;

  // Reset regex state for global search
  TAG_FIND_REGEX.lastIndex = 0;

  while ((match = TAG_FIND_REGEX.exec(label)) !== null) {
    // Add text segment before the tag
    if (match.index > lastIndex) {
      segments.push({
        type: 'text',
        content: label.substring(lastIndex, match.index),
      });
    }

    // Add tag segment
    const tagName = match[1];
    const colorString = match[2];
    const badgeColor: BadgeColor =
      colorString && validBadgeColors.includes(colorString as BadgeColor)
        ? (colorString as BadgeColor)
        : 'gray';
    segments.push({ type: 'tag', name: tagName, color: badgeColor });

    lastIndex = TAG_FIND_REGEX.lastIndex;
  }

  // Add remaining text segment after the last tag
  if (lastIndex < label.length) {
    segments.push({ type: 'text', content: label.substring(lastIndex) });
  }

  // Trim whitespace around text segments and filter empty ones
  return segments
    .map(seg => {
      if (seg.type === 'text' && seg.content) {
        seg.content = seg.content.trim();
      }
      return seg;
    })
    .filter(
      seg =>
        seg.type === 'tag' ||
        (seg.type === 'text' && seg.content && seg.content.length > 0),
    );
}

export function reconstructLabelFromSegments(
  segments: ParsedSegment[],
): string {
  return segments
    .filter(seg => seg.type === 'text')
    .map(seg => seg.content)
    .join(' ')
    .trim();
}

export function getLabelText(label: string): string {
  const segments = referentialLabelParser(label);
  return reconstructLabelFromSegments(segments);
}
